import React, { FC, useCallback, useState } from 'react';
import { Switch } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useApiError, useLoading } from '../../../../providers';
import { getTranslation } from '../../../../utils';
import { NewRepresentative, Representative } from '../../RepresentativesModels';
import { reSendRepresentativelink, revokeRepresentative } from '../../RepresentativesService';
import { AddItemResult } from '../../../../models';
import { ActivateRepresentativeDialog, RevokeRepresentativeDialog } from '../ActivateRevokeRepresentativeDialog';
import { useCommonStyles } from '../../../../styles';

interface Props {
  translation: any;
  representative: Representative;
  onSwitchChange: () => void;
}

export const ActivateRevokeSwitch: FC<Props> = ({ translation, representative, onSwitchChange }) => {
  const commonClasses = useCommonStyles();
  const { setIsLoading } = useLoading();
  const { addError } = useApiError();
  const { enqueueSnackbar } = useSnackbar();
  const [openRevokeDialogTrigger, setOpenRevokeDialogTrigger] = useState<boolean>(false);
  const [openActivateDialogTrigger, setOpenActivateDialogTrigger] = useState<boolean>(false);
  const [status, setStatus] = useState(representative.accessRevokedDate ? 'Inactive' : 'Active');

  const representativeRevokeCallback = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await revokeRepresentative(representative.id);
      switch (response.data.status) {
        case AddItemResult.Success:
          enqueueSnackbar(response.message ? getTranslation(translation, response.message) : translation.text_6503, { variant: 'success' });
          break;
        case AddItemResult.Failure:
          enqueueSnackbar(response.message ? getTranslation(translation, response.message) : translation.text_6504, { variant: 'error' });
          break;

        case AddItemResult.ValidationError:
          enqueueSnackbar(response.message ? getTranslation(translation, response.message) : translation.text_508, { variant: 'error' });
          break;
        default:
          break;
      }
      setOpenRevokeDialogTrigger(false);
      setStatus('Inactive');
      onSwitchChange();
    } catch (error) {
      addError(JSON.stringify(error.response));
    } finally {
      setIsLoading(false);
    }
  }, [enqueueSnackbar, representative, addError, translation, setIsLoading, onSwitchChange]);

  const representativeResendLinkCallback = useCallback(async () => {
    setIsLoading(true);
    try {
      const assignedPartnershipCodes: string[] = [];
      if (representative.representativeAssignedPartnerships) {
        representative.representativeAssignedPartnerships.forEach((partnership) => {
          if (partnership.partnershipCode) {
            assignedPartnershipCodes.push(partnership.partnershipCode);
          }
        });
      }

      const newRepresentative: NewRepresentative = {
        email: representative.email,
        displayName: representative.displayName,
        password: '',
        partnerFID: representative.partnerFID,
        assignedPartnershipCodes,
      };
      const response = await reSendRepresentativelink(newRepresentative);
      switch (response.data.status) {
        case AddItemResult.Success:
          enqueueSnackbar(response.message ? getTranslation(translation, response.message) : translation.text_6505, { variant: 'success' });
          break;
        case AddItemResult.Failure:
          enqueueSnackbar(response.message ? getTranslation(translation, response.message) : translation.text_6506, { variant: 'error' });
          break;

        case AddItemResult.ValidationError:
          enqueueSnackbar(response.message ? getTranslation(translation, response.message) : translation.text_508, { variant: 'error' });
          break;
        default:
          break;
      }
      setOpenActivateDialogTrigger(false);
      setStatus('Active');
      onSwitchChange();
    } catch (error) {
      addError(JSON.stringify(error.response));
    } finally {
      setIsLoading(false);
    }
  }, [enqueueSnackbar, representative, addError, translation, setIsLoading, onSwitchChange]);

  const handleSwitchChange = useCallback(
    async (event) => {
      event.stopPropagation();
      if (representative.accessRevokedDate) {
        setOpenActivateDialogTrigger(true);
      } else {
        setOpenRevokeDialogTrigger(true);
      }
    },
    [representative],
  );

  return (
    <>
      <Switch checked={status === 'Active'} classes={{ root: commonClasses.switchRoot }} onClick={handleSwitchChange} id={representative.id.toString()} />

      <ActivateRepresentativeDialog open={openActivateDialogTrigger} translation={translation} onClose={() => setOpenActivateDialogTrigger(false)} onActivate={representativeResendLinkCallback} />

      <RevokeRepresentativeDialog open={openRevokeDialogTrigger} translation={translation} onClose={() => setOpenRevokeDialogTrigger(false)} onRevoke={representativeRevokeCallback} />
    </>
  );
};
